// src/Components/Modules/HR/RosterScheduler.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import {
  FaCalendar,
  FaSave,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaClock,
  FaCalendarAlt,
  FaSun,
  FaMoon,
  FaExchangeAlt,
  FaSync,
  FaUserClock,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaUser
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.2, 3),
  background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  color: "#1b5e20",
  "&:hover": {
    background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
    boxShadow: "0 6px 20px rgba(67, 233, 123, 0.4)",
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(0.6, 1.5),
  minWidth: "auto",
  ...(variant === "edit" && {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    },
  }),
}));

const RosterScheduler = () => {
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    start_date: "",
    end_date: "",
  });
  const [entryDialog, setEntryDialog] = useState(null);
  const [entryForm, setEntryForm] = useState({
    employee_id: "",
    date: "",
    shift_type: "day",
    start_time: "",
    end_time: "",
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await AxiosAuthInstance.get("add_employee");
      setEmployees(
        Array.isArray(res.data.employee) ? res.data.employee : []
      );
    } catch (err) {
      console.error("Failed to load employees", err);
    }
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please enter a roster name",
      });
      return false;
    }
    if (!form.start_date) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select a start date",
      });
      return false;
    }
    if (!form.end_date) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select an end date",
      });
      return false;
    }
    if (form.start_date > form.end_date) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Dates",
        text: "End date cannot be before start date",
      });
      return false;
    }
    if (entries.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Entries",
        text: "Please add at least one employee shift entry",
      });
      return false;
    }
    return true;
  };

  const publish = async () => {
    if (!validateForm()) return;

    const result = await Swal.fire({
      title: "Publish Roster?",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Roster Name:</strong> ${form.name}</p>
          <p><strong>Period:</strong> ${form.start_date} to ${form.end_date}</p>
          <p><strong>Total Entries:</strong> ${entries.length}</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#43e97b",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Publish",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setPublishLoading(true);
      try {
        const payload = {
          ...form,
          entries: entries.map((entry) => ({
            employee_id: entry.employee_id,
            date: entry.date,
            shift_type: entry.shift_type,
            start_time: entry.start_time,
            end_time: entry.end_time,
          })),
        };
        await AxiosAuthInstance.post("hr/roster", payload);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Roster published successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        // Reset form
        setForm({ name: "", start_date: "", end_date: "" });
        setEntries([]);
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to publish roster";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setPublishLoading(false);
      }
    }
  };

  const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
      borderRadius: theme.spacing(3),
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
  }));

  const FormFieldWrapper = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2.5),
  }));

  const openEntryDialog = (entry = null) => {
    if (entry) {
      setEntryForm(entry);
      setEntryDialog({ mode: "edit", index: entries.indexOf(entry) });
    } else {
      setEntryForm({
        employee_id: "",
        date: "",
        shift_type: "day",
        start_time: "",
        end_time: "",
      });
      setEntryDialog({ mode: "add" });
    }
  };

  const saveEntry = () => {
    if (!entryForm.employee_id) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select an employee",
      });
      return;
    }
    if (!entryForm.date) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select a date",
      });
      return;
    }

    if (entryDialog.mode === "edit") {
      const updated = [...entries];
      updated[entryDialog.index] = entryForm;
      setEntries(updated);
    } else {
      setEntries([...entries, entryForm]);
    }
    setEntryDialog(null);
  };

  const deleteEntry = (index) => {
    Swal.fire({
      title: "Delete Entry?",
      text: "Are you sure you want to delete this entry?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const updated = entries.filter((_, i) => i !== index);
        setEntries(updated);
      }
    });
  };

  const filtered = useMemo(() => {
    return entries.filter((entry) => {
      const employee = employees.find((e) => e.id === entry.employee_id);
      const employeeName = employee?.employee_name?.toLowerCase() || "";
      const searchTerm = search.toLowerCase();
      return employeeName.includes(searchTerm);
    });
  }, [entries, employees, search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const getShiftIcon = (shiftType) => {
    switch (shiftType?.toLowerCase()) {
      case "day":
        return <FaSun />;
      case "night":
        return <FaMoon />;
      case "rotating":
        return <FaExchangeAlt />;
      default:
        return <FaClock />;
    }
  };

  const getShiftColor = (shiftType) => {
    switch (shiftType?.toLowerCase()) {
      case "day":
        return { bg: "#fff3e0", color: "#ef6c00" };
      case "night":
        return { bg: "#e3f2fd", color: "#1565c0" };
      case "rotating":
        return { bg: "#f3e5f5", color: "#7b1fa2" };
      default:
        return { bg: "#f5f5f5", color: "#757575" };
    }
  };

  const getInitials = (name) => {
    if (!name) return "N/A";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe",
      "#43e97b", "#fa709a", "#fee140", "#30cfd0",
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Calculate stats
  const stats = useMemo(() => {
    const dayShifts = entries.filter((e) => e.shift_type === "day").length;
    const nightShifts = entries.filter((e) => e.shift_type === "night").length;
    const uniqueEmployees = new Set(entries.map((e) => e.employee_id)).size;
    return {
      total: entries.length,
      dayShifts,
      nightShifts,
      uniqueEmployees,
    };
  }, [entries]);

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Shift & Roster" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaUserClock style={{ marginRight: 10, verticalAlign: "middle" }} />
              Shift & Roster Scheduler
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Create and manage employee shift schedules
            </Typography>
          </Box>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaUsers size={28} color="#2196f3" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#1565c0" sx={{ mb: 0.5 }}>
              {stats.uniqueEmployees}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Employees
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e8f5e9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaCalendarAlt size={28} color="#4caf50" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#2e7d32" sx={{ mb: 0.5 }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Total Shifts
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#fff3e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaSun size={28} color="#ff9800" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#ef6c00" sx={{ mb: 0.5 }}>
              {stats.dayShifts}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Day Shifts
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#f3e5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaMoon size={28} color="#7b1fa2" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#7b1fa2" sx={{ mb: 0.5 }}>
              {stats.nightShifts}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Night Shifts
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Roster Details Form */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          <FaCalendar style={{ marginRight: 8, verticalAlign: "middle" }} />
          Roster Details
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Roster Name"
              placeholder="e.g., January 2024 Roster"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <PrimaryButton
              fullWidth
              variant="contained"
              onClick={publish}
              disabled={publishLoading}
              startIcon={publishLoading ? null : <FaSave />}
              sx={{ height: 40 }}
            >
              {publishLoading ? "Publishing..." : "Publish Roster"}
            </PrimaryButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Entries Management */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            <FaUsers style={{ marginRight: 8, verticalAlign: "middle" }} />
            Shift Entries ({entries.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            onClick={() => openEntryDialog()}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              },
            }}
          >
            Add Entry
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Search */}
        <TextField
          placeholder="Search by employee name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        {/* Table */}
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <StyledTableCell width={80}>SL</StyledTableCell>
                <StyledTableCell>Employee</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell>Shift Type</StyledTableCell>
                <StyledTableCell>Start Time</StyledTableCell>
                <StyledTableCell>End Time</StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <FaUsers style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No shift entries
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click "Add Entry" to create shift schedules
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => {
                  const employee = employees.find((e) => e.id === row.employee_id);
                  const employeeName = employee?.employee_name || "N/A";
                  const shiftColor = getShiftColor(row.shift_type);

                  return (
                    <StyledTableRow key={index} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              bgcolor: getColorFromName(employeeName),
                              width: 40,
                              height: 40,
                              fontWeight: 600,
                            }}
                          >
                            {getInitials(employeeName)}
                          </Avatar>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {employeeName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FaCalendarAlt style={{ fontSize: 14, color: "#757575" }} />
                          <Typography variant="body2">
                            {formatDate(row.date)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getShiftIcon(row.shift_type)}
                          label={row.shift_type || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: shiftColor.bg,
                            color: shiftColor.color,
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.start_time || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.end_time || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => openEntryDialog(row)}
                              sx={{ color: "#667eea" }}
                            >
                              <FaEdit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => deleteEntry(entries.indexOf(row))}
                              sx={{ color: "#f44336" }}
                            >
                              <FaTrash fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </StyledTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {filtered.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filtered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: 1,
                borderColor: "divider",
              }}
            />
          )}
        </StyledTableContainer>

        <Alert severity="info" icon={<FaInfoCircle />} sx={{ mt: 2 }}>
          Backend accepts entries in format: <code>{`{entries: [{employee_id, date, shift_type, start_time, end_time}]}`}</code>
        </Alert>
      </Paper>

      {/* Add/Edit Entry Dialog */}
      <StyledDialog
  open={!!entryDialog}
  onClose={() => setEntryDialog(null)}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 3,
      overflow: 'hidden',
    }
  }}
>
  <DialogTitle 
    sx={{ 
      bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "white",
      py: 2.5,
      px: 3,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {entryDialog?.mode === "edit" ? <FaEdit /> : <FaPlus />}
      </Box>
      <Typography variant="h6" fontWeight={600}>
        {entryDialog?.mode === "edit" ? "Edit Shift Entry" : "Add Shift Entry"}
      </Typography>
    </Box>
  </DialogTitle>
  
  <DialogContent 
  sx={{ 
    pt: 3, 
    px: 3,
    '& .MuiDialogContent-root': {
      paddingTop: '24px!important',
    }
  }}>
    <Stack spacing={2.5} sx={{pt: 2}}>
      {/* Employee Field */}
      <FormFieldWrapper>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1 }}>
          <FaUser style={{ marginRight: 6, verticalAlign: "middle" }} />
          Employee
        </Typography>
        <TextField
          select
          fullWidth
          value={entryForm.employee_id}
          onChange={(e) => setEntryForm({ ...entryForm, employee_id: e.target.value })}
          variant="outlined"
          placeholder="Select employee"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea',
              },
            },
          }}
        >
          {employees.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                  {getInitials(emp.employee_name)}
                </Avatar>
                {emp.employee_name}
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </FormFieldWrapper>

      {/* Date Field */}
      <FormFieldWrapper>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1 }}>
          <FaCalendarAlt style={{ marginRight: 6, verticalAlign: "middle" }} />
          Date
        </Typography>
        <TextField
          type="date"
          fullWidth
          value={entryForm.date}
          onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea',
              },
            },
          }}
        />
      </FormFieldWrapper>

      {/* Shift Type Field */}
      <FormFieldWrapper>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1 }}>
          <FaClock style={{ marginRight: 6, verticalAlign: "middle" }} />
          Shift Type
        </Typography>
        <TextField
          select
          fullWidth
          value={entryForm.shift_type}
          onChange={(e) => setEntryForm({ ...entryForm, shift_type: e.target.value })}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea',
              },
            },
          }}
        >
          <MenuItem value="day">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaSun style={{ color: "#ff9800" }} />
              <span>Day Shift</span>
            </Box>
          </MenuItem>
          <MenuItem value="night">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaMoon style={{ color: "#3f51b5" }} />
              <span>Night Shift</span>
            </Box>
          </MenuItem>
          <MenuItem value="rotating">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaExchangeAlt style={{ color: "#9c27b0" }} />
              <span>Rotating Shift</span>
            </Box>
          </MenuItem>
        </TextField>
      </FormFieldWrapper>

      {/* Time Fields */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1 }}>
          <FaClock style={{ marginRight: 6, verticalAlign: "middle" }} />
          Shift Timing
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              type="time"
              fullWidth
              label="Start Time"
              value={entryForm.start_time}
              onChange={(e) => setEntryForm({ ...entryForm, start_time: e.target.value })}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4caf50',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4caf50',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="time"
              fullWidth
              label="End Time"
              value={entryForm.end_time}
              onChange={(e) => setEntryForm({ ...entryForm, end_time: e.target.value })}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f44336',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f44336',
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  </DialogContent>
  
  <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
    <Button
      onClick={() => setEntryDialog(null)}
      variant="outlined"
      sx={{ 
        textTransform: "none", 
        fontWeight: 600,
        px: 3,
        py: 1.2,
        borderRadius: 2,
        borderColor: '#757575',
        color: '#757575',
        '&:hover': {
          borderColor: '#757575',
          bgcolor: 'rgba(117, 117, 117, 0.04)',
        },
      }}
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      onClick={saveEntry}
      startIcon={<FaSave />}
      sx={{
        textTransform: "none",
        fontWeight: 600,
        px: 3,
        py: 1.2,
        borderRadius: 2,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        '&:hover': {
          background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
        },
      }}
    >
      Save Entry
    </Button>
  </DialogActions>
</StyledDialog>
    </Box>
  );
};

export default RosterScheduler;